import React, { useEffect, useState } from "react";
import { useLanguage } from "src/context/LanguageContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import {
  Award,
  BadgeCheck,
  BookOpen,
  Briefcase,
  Building,
  CalendarDays,
  Download,
  FileText,
  GraduationCap,
  Mail,
  MapPin,
  Paperclip,
  Phone,
  Play,
  Send,
  Star,
  User,
  Users2,
} from "lucide-react";
import { Button } from "src/components/ui/button";
import axios from "axios";
import { BaseUrlApi, ErrorMessage, UrlImage } from "src/lib/api";
import { useAuth } from "src/context/AuthContext";
import { useAPI } from "src/context/APIContext";
import { toast } from "sonner";
import { Avatar, Image } from "antd";
import { cn, formatPrice } from "src/lib/utils";
import { Badge } from "src/components/ui/badge";
import { Separator } from "src/components/ui/separator";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "src/components/ui/tabs";
import TimeSelectorTeacher from "src/components/common/TimeSelectorTeacher";
import { Skeleton } from "src/components/ui/skeleton";
import RateButton from "src/components/common/RateButton";
import dayjs from "dayjs";
import TeacherProfileDialog from "./_components/TeacherProfileDialog";
import { useLocation } from "react-router-dom";
import MarkPaid from "./_components/MarkPaid";

export default function TeacherSettings() {
  const { language } = useLanguage();

  const location = useLocation();
  const userData = location.state?.userData;

  const { user: currentUser } = useAuth();

  const user = userData ? userData : currentUser;

  const { categoriesItems, coursesItems, TeacherCoursesItems } = useAPI();
  const { categories } = categoriesItems;
  const { courses } = coursesItems;
  const { teacherCourses } = TeacherCoursesItems;

  const [loadingTeacherData, setLoadingTeacherData] = useState(true);
  const [teacherData, setTeacherData] = useState([]);
  const getAllTeacherData = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/teacher-profiles/${user.id}`
      );
      setTeacherData(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingTeacherData(false);
    }
  };

  const [loadingStudents, setLoadingStudents] = useState(true);
  const [students, setStudents] = useState([]);
  const getAllStudents = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/teacher/courses`);
      setStudents(data.data);
    } catch (error) {
      toast("Error", {
        description: ErrorMessage(error),
      });
    } finally {
      setLoadingStudents(false);
    }
  };

  const [schedule, setSchedule] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetchSchedule = async () => {
    try {
      const { data } = await axios.get(`${BaseUrlApi}/teacher/schedule`);
      setSchedule(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoading(false);
    }
  };

  const [transaction, setTransaction] = useState([]);
  const [loadingTransaction, setLoadingTransaction] = useState(true);
  const fetchTransaction = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/user/transactions/${user?.id}`
      );

      const transactionOfMonth = data.data.filter((transaction) =>
        dayjs(transaction?.month).isSame(dayjs(), "month")
      );

      setTransaction(transactionOfMonth);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoadingTransaction(false);
    }
  };

  const [salary, setSalary] = useState({});
  const [loadingSalary, setLoadingSalary] = useState(false);
  const fetchSalary = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/teacher-payments/teacher/${user?.id}`
      );

      const salaryOfMonth = data.data.find((salary) =>
        dayjs(salary?.month).isSame(dayjs(), "month")
      );

      setSalary(salaryOfMonth);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoadingSalary(false);
    }
  };

  useEffect(() => {
    getAllStudents();
    getAllTeacherData();
    fetchSchedule();
    fetchTransaction();
    fetchSalary();
  }, []);

  const isSameUser = currentUser?.id === user?.id;
  const isAdmin = currentUser.role === "admin";

  const [open, setOpen] = useState(false);
  const handleMarkPaid = async () => {
    setLoading(true);
    try {
      await axios.put(
        `${BaseUrlApi}/teacher-payments/teacher-hold/${salary.hold.id}`
      );
      toast.success("Success", {
        description: "Salary marked as paid",
      });
      setOpen(false);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object") toast("Error", { description: result });
    } finally {
      setLoading(false);
    }
  };

  if (loadingTeacherData)
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="w-full h-40 col-span-1 lg:col-span-2" />
          <Skeleton className="w-full h-40 col-span-1" />
        </div>
        <Skeleton className="w-full h-32 col-span-1" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="space-y-6">
            <Skeleton className="w-full h-40 col-span-1" />
            <Skeleton className="w-full h-40 col-span-1" />
          </div>
          <div className="w-full col-span-1 lg:col-span-2">
            <Skeleton className="w-full h-full" />
          </div>
        </div>
      </div>
    );

  return (
    <div className="space-y-6" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="text-2xl font-bold">
          Profile {teacherData?.user.name}
        </div>

        {isSameUser || isAdmin ? (
          <TeacherProfileDialog
            teacherData={teacherData}
            getAllTeacherData={getAllTeacherData}
          />
        ) : null}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Card */}
        <Card className="overflow-hidden col-span-1 lg:col-span-2">
          <CardContent className="p-4 space-y-4 overflow-auto flex flex-col justify-between h-full">
            <div className="flex gap-4 justify-between whitespace-nowrap">
              <div className="flex items-center gap-4">
                {/* Avatar */}
                {teacherData?.user?.image ? (
                  <Image
                    wrapperStyle={{
                      width: "6rem",
                      height: "6rem",
                      borderRadius: "9999px",
                      overflow: "hidden",
                      border: "1px solid hsl(var(--border))",
                    }}
                    src={`${UrlImage}${teacherData?.user?.image}`}
                  />
                ) : (
                  <Avatar
                    src={`${UrlImage}${teacherData?.user?.image}`}
                    className={cn(
                      "min-w-24 sm:w-24 h-24 text-lg rounded-full flex items-center justify-center font-medium",
                      "bg-blue-100 text-blue-600",
                      "dark:bg-blue-900 dark:text-blue-300"
                    )}
                  >
                    {teacherData.user?.name.substring(0, 2).toUpperCase()}
                  </Avatar>
                )}
                {/* Details */}
                <div className="space-y-1">
                  {/* Head */}
                  <div className="flex items-center gap-2">
                    <div className="bg-yellow-400/20 py-1 px-2 text-yellow-400 rounded-full flex items-center gap-1 font-semibold text-xs">
                      <Star className="size-4 fill-yellow-400" />{" "}
                      {teacherData.rate}
                    </div>
                    {teacherData.rate >= 4.5 && (
                      <div className="bg-green-400/20 py-1 px-2 text-green-500 rounded-full flex items-center gap-1 font-semibold text-xs">
                        Best teacher for the second month in a row
                      </div>
                    )}

                    {/* <p className="text-xs font-semibold">
                  Based on <span className="text-blue-600">142 comments</span>
                </p> */}
                  </div>
                  {/* Mid */}
                  <div className="text-2xl font-bold flex items-center gap-1">
                    {teacherData.user?.name}
                    <BadgeCheck className="text-white fill-blue-600 stroke-[1.5] size-7" />
                  </div>
                  {/* Footer */}
                  <p className="text-muted-foreground text-sm font-semibold">
                    {teacherData.name_job}
                  </p>
                </div>
              </div>
            </div>
            {/* Badge */}
            <div className="flex items-center gap-2 flex-wrap">
              {teacherData.subjects.map((subject, index) => (
                <div
                  key={index}
                  className="border-2 border-primary rounded-full w-fit text-xs font-bold py-1 px-3"
                >
                  {subject}
                </div>
              ))}
            </div>
            {/* Other Details */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="text-muted-foreground text-xs font-semibold flex items-center gap-1">
                <GraduationCap className="size-5" />
                {teacherData !== 0 && "+"}
                {Number(teacherData.year_experience)} Years of experience
              </div>
              <div className="text-muted-foreground text-xs font-semibold flex items-center gap-1">
                <BookOpen className="size-5" />
                {teacherCourses.length} Courses
              </div>
              <div className="text-muted-foreground text-xs font-semibold flex items-center gap-1">
                <Users2 className="size-5" />
                {students.length >= 1 && "+"}
                {students.length} Students
              </div>
            </div>
          </CardContent>
        </Card>
        {/* Contact Information */}
        <Card className="h-fit">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl flex items-center gap-2">
              <User className="size-5" /> Contact Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <Mail className="size-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Email</p>
                <p className="text-sm text-primary">
                  {teacherData.user?.email || (
                    <span className="text-muted-foreground italic">
                      Not provided
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Phone className="size-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Phone</p>
                <p className="text-sm">
                  {teacherData.user?.phone || (
                    <span className="text-muted-foreground italic">
                      Not provided
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="size-5 text-muted-foreground mt-0.5" />
              <div>
                <p className="text-sm font-medium">Address</p>
                <p className="text-sm">
                  {teacherData.address || (
                    <span className="text-muted-foreground italic">
                      Not provided
                    </span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Introduction */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <p className="font-bold text-2xl">Introduction</p>
          <p className="font-medium text-sm text-muted-foreground">
            {teacherData.description}
          </p>
        </CardContent>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          {/* Personal Details */}
          <Card className="h-fit">
            <CardHeader className="pb-2">
              <CardTitle className="text-xl flex items-center gap-2">
                <User className="size-5" /> Personal Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Age</p>
                <p className="text-sm">
                  {teacherData.age || (
                    <span className="text-muted-foreground italic">
                      Not provided
                    </span>
                  )}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Languages</p>
                <div className="flex flex-wrap gap-1 justify-end">
                  {teacherData.languages.map((language, index) => (
                    <Badge key={index} variant="outline" className="text-xs">
                      {language}
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Hours per Week</p>
                <p className="text-sm">
                  {Number(teacherData.hour_works) || 0} hours
                </p>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Hold Rate</p>
                <Badge variant="secondary" className="font-bold">
                  ${teacherData.teacher_hold_rate}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Pay Rate</p>
                <Badge variant="secondary" className="font-bold">
                  ${teacherData.teacher_pay_rate}
                </Badge>
              </div>
              <Separator className="my-2" />

              {teacherData.programs.length !== 0 ? (
                <div>
                  <p className="text-sm font-medium mb-2">Programs</p>
                  <div className="flex flex-wrap gap-2">
                    {teacherData.programs.map((program, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="bg-primary/5"
                      >
                        {program}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : null}

              {teacherData.courses_id && teacherData.courses_id.length !== 0 ? (
                <div>
                  <p className="text-sm font-medium mb-2">Courses</p>
                  <div className="flex flex-wrap gap-2">
                    {courses
                      .filter((course) =>
                        teacherData.courses_id?.includes(course.id)
                      )
                      .map((course, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="bg-primary/5"
                        >
                          {course.title[language] || course.title.en}
                        </Badge>
                      ))}
                  </div>
                </div>
              ) : null}

              {teacherData.categories_id && teacherData.categories_id !== 0 ? (
                <div>
                  <p className="text-sm font-medium mb-2">Categories</p>
                  <div className="flex flex-wrap gap-2">
                    {categories
                      .filter((category) =>
                        teacherData.categories_id?.includes(category.id)
                      )
                      .map((category, index) => (
                        <Badge
                          key={index}
                          variant="outline"
                          className="bg-primary/5"
                        >
                          {category.title[language] || category.title.en}
                        </Badge>
                      ))}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
          {/* Files & Resources */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-xl flex items-center gap-2">
                <FileText className="size-5" /> Files & Resources
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* CV */}
              <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-md">
                    <FileText className="size-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">CV/Resume</p>
                    <p className="text-xs text-muted-foreground">
                      {teacherData.cv ? "Teacher CV" : "No CV uploaded"}
                    </p>
                  </div>
                </div>
                {teacherData.cv && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    asChild
                  >
                    <a
                      href={`${UrlImage}${teacherData.cv}`}
                      download="myfile.pdf"
                      target="_blank"
                    >
                      <Download className="size-4" />
                    </a>
                  </Button>
                )}
              </div>

              {/* Demo Video */}
              <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/10 p-2 rounded-md">
                    <Play className="size-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Demo Video</p>
                    <p className="text-xs text-muted-foreground">
                      {teacherData.demo_video
                        ? "Teaching demonstration"
                        : "No demo video uploaded"}
                    </p>
                  </div>
                </div>
                {teacherData.demo_video && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8 w-8 p-0"
                    asChild
                  >
                    <a
                      href={`${UrlImage}${teacherData.demo_video}`}
                      download="myfile.pdf"
                      target="_blank"
                    >
                      <Play className="size-4" />
                    </a>
                  </Button>
                )}
              </div>

              {/* Additional Files */}
              {teacherData.files && teacherData.files.length > 0 && (
                <>
                  <p className="text-sm font-medium">Additional Files</p>
                  <div className="space-y-2">
                    {teacherData.files.map((file, index) => (
                      <div
                        key={index}
                        className="bg-muted/50 rounded-lg p-3 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <div className="bg-primary/10 p-2 rounded-md">
                            <FileText className="size-5 text-primary" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">
                              Files {index + 1}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {file.size}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0"
                          asChild
                        >
                          <a
                            href={`${UrlImage}${file}`}
                            download="myfile.pdf"
                            target="_blank"
                          >
                            <Download className="size-4" />
                          </a>
                        </Button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Qualifications & Experience */}
        <Tabs
          defaultValue="qualifications"
          className="w-full col-span-1 lg:col-span-2"
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="qualifications">Qualifications</TabsTrigger>
            <TabsTrigger value="experience">Experience</TabsTrigger>
          </TabsList>

          {/* Qualifications Tab */}
          <TabsContent value="qualifications" className="mt-4 space-y-4">
            {teacherData.teacher_qualifications.map((qualification, index) => (
              <Card key={index} className="overflow-hidden">
                <div className="bg-primary/5 p-4 flex items-start gap-4">
                  <div className="bg-primary/10 p-3 rounded-full">
                    <Award className="size-6 text-primary" />
                  </div>
                  <div className="space-y-1 w-full">
                    <h3 className="font-bold text-lg">
                      {qualification.qualification}
                    </h3>
                    <div className="flex items-center">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-1">
                        <Building className="size-4" />
                        <span>{qualification.university}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground flex-1">
                        <CalendarDays className="size-4" />
                        <span>
                          Graduated in {qualification.graduation_year}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <CardContent className="pt-4">
                  <p className="text-sm font-medium mb-2">Courses</p>
                  <div className="flex flex-wrap gap-2">
                    {qualification.courses.map((course, courseIndex) => (
                      <Badge key={courseIndex} variant="secondary">
                        {course}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}

            {teacherData.teacher_qualifications.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <GraduationCap className="size-12 mx-auto mb-2 opacity-20" />
                <p>No qualifications have been added yet.</p>
              </div>
            )}
          </TabsContent>

          {/* Experience Tab */}
          <TabsContent value="experience" className="mt-4">
            <div className="relative pl-6 border-l-2 border-primary/20 space-y-6 py-2">
              {teacherData.teacher_experiences.map((experience, index) => (
                <div key={index} className="relative">
                  <div className="absolute -left-[25px] top-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Briefcase className="size-5 text-primary" />
                  </div>
                  <Card>
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-bold text-lg">
                            {experience.institution}
                          </h3>
                          <p className="text-sm text-muted-foreground capitalize">
                            {experience.type} Teaching • {experience.ages} age
                            group
                          </p>
                        </div>
                        <Badge variant="outline" className="bg-primary/5">
                          {experience.year}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}

              {teacherData.teacher_experiences.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Briefcase className="size-12 mx-auto mb-2 opacity-20" />
                  <p>No experience has been added yet.</p>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      {/* Lecture schedule */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <p className="font-bold text-2xl">Lecture schedule</p>
          <div className="">
            <TimeSelectorTeacher data={schedule} loading={loading} />
          </div>
        </CardContent>
      </Card>
      {/* Money management */}
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-2">
          <p className="font-bold text-2xl">Money management</p>
          {isAdmin && (
            <MarkPaid
              salary={salary}
              onClick={handleMarkPaid}
              data={teacherData}
              open={open}
              setOpen={setOpen}
            />
          )}
        </div>

        {loadingSalary ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Skeleton className="w-full h-40" />
            <Skeleton className="w-full h-40" />
            <Skeleton className="w-full h-40" />
            <Skeleton className="w-full h-40" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Card1 */}
            <Card className="overflow-hidden bg-blue-600">
              <CardContent className="p-5 flex flex-col items-center justify-center gap-2">
                <svg
                  width="27"
                  height="28"
                  viewBox="0 0 27 28"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.3088 0.825012L20.2838 6.39501L22.905 5.50126L25.2975 12.5H26.5V27.5H0.25V12.5H0.8875V12.4875L1.6975 12.495L16.3088 0.825012ZM10.1213 12.5H22.6562L21.36 8.70876L19.4575 9.31751L10.1213 12.5ZM8.17375 10.5213L17.8125 7.23751L15.8075 4.42501L8.17375 10.5213ZM5.25 15H2.75V17.5C3.41304 17.5 4.04893 17.2366 4.51777 16.7678C4.98661 16.2989 5.25 15.6631 5.25 15ZM17.75 20C17.75 19.4255 17.6368 18.8566 17.417 18.3258C17.1971 17.795 16.8748 17.3127 16.4686 16.9064C16.0623 16.5002 15.58 16.1779 15.0492 15.958C14.5184 15.7382 13.9495 15.625 13.375 15.625C12.8005 15.625 12.2316 15.7382 11.7008 15.958C11.17 16.1779 10.6877 16.5002 10.2814 16.9064C9.87515 17.3127 9.55289 17.795 9.33303 18.3258C9.11316 18.8566 9 19.4255 9 20C9 21.1603 9.46094 22.2731 10.2814 23.0936C11.1019 23.9141 12.2147 24.375 13.375 24.375C14.5353 24.375 15.6481 23.9141 16.4686 23.0936C17.2891 22.2731 17.75 21.1603 17.75 20ZM24 25V22.5C23.337 22.5 22.7011 22.7634 22.2322 23.2322C21.7634 23.7011 21.5 24.337 21.5 25H24ZM21.5 15C21.5 15.6631 21.7634 16.2989 22.2322 16.7678C22.7011 17.2366 23.337 17.5 24 17.5V15H21.5ZM2.75 25H5.25C5.25 24.337 4.98661 23.7011 4.51777 23.2322C4.04893 22.7634 3.41304 22.5 2.75 22.5V25Z"
                    fill="white"
                  />
                </svg>
                <p className="font-semibold text-xs text-gray-300">Total due</p>
                <div className="flex items-center justify-center flex-wrap gap-2">
                  <div className="font-bold text-2xl text-white">
                    {formatPrice(Number(salary?.net_salary) || 0)}
                  </div>
                  {/* <div className="border-2 border-green-400 text-green-400 py-1 px-2 rounded-full text-xs font-semibold h-fit mt-1">
                +0.7%
              </div> */}
                </div>
              </CardContent>
            </Card>
            {/* Card2 */}
            <Card className="overflow-hidden">
              <CardContent className="p-5 flex flex-col items-center justify-center gap-2">
                <svg
                  width="31"
                  height="30"
                  viewBox="0 0 31 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_192_1741)">
                    <path
                      d="M20.4824 7.5H29.0539V16.0714"
                      stroke="#2155CD"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M29.0534 7.5L16.9463 19.6071C16.746 19.8035 16.4767 19.9134 16.1963 19.9134C15.9158 19.9134 15.6466 19.8035 15.4463 19.6071L10.5177 14.6786C10.3174 14.4823 10.0482 14.3723 9.76772 14.3723C9.48727 14.3723 9.218 14.4823 9.01772 14.6786L1.19629 22.5"
                      stroke="#2155CD"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_192_1741">
                      <rect
                        width="30"
                        height="30"
                        fill="white"
                        transform="translate(0.125)"
                      />
                    </clipPath>
                  </defs>
                </svg>

                <p className="font-semibold text-xs text-gray-500">
                  Total Bonuses
                </p>
                <div className="flex items-center justify-center flex-wrap gap-2">
                  <div className="font-bold text-2xl">
                    <span className="text-green-500">+</span>
                    {formatPrice(Number(salary?.total_bonus) || 0)}
                  </div>
                  {/* <div className="border-2 border-green-400 text-green-400 py-1 px-2 rounded-full text-xs font-semibold h-fit mt-1">
                +0.1%
              </div> */}
                </div>
              </CardContent>
            </Card>
            {/* Card3 */}
            <Card className="overflow-hidden">
              <CardContent className="p-5 flex flex-col items-center justify-center gap-2">
                <svg
                  width="31"
                  height="30"
                  viewBox="0 0 31 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_192_1745)">
                    <path
                      d="M21.2324 22.5H29.8039V13.9286"
                      stroke="#2155CD"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M29.8034 22.5L17.6963 10.3929C17.496 10.1965 17.2267 10.0866 16.9463 10.0866C16.6658 10.0866 16.3966 10.1965 16.1963 10.3929L11.2677 15.3214C11.0674 15.5177 10.7982 15.6277 10.5177 15.6277C10.2373 15.6277 9.968 15.5177 9.76772 15.3214L1.94629 7.5"
                      stroke="#2155CD"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_192_1745">
                      <rect
                        width="30"
                        height="30"
                        fill="white"
                        transform="translate(0.875)"
                      />
                    </clipPath>
                  </defs>
                </svg>

                <p className="font-semibold text-xs text-gray-500">
                  Total Deductions
                </p>
                <div className="flex items-center justify-center flex-wrap gap-2">
                  <div className="font-bold text-2xl">
                    <span className="text-red-500">-</span>
                    {formatPrice(Number(salary?.total_deduction) || 0)}
                  </div>
                  {/* <div className="border-2 border-red-400 text-red-400 py-1 px-2 rounded-full text-xs font-semibold h-fit mt-1">
                +0.7%
              </div> */}
                </div>
              </CardContent>
            </Card>
            {/* Card4 */}
            <Card className="overflow-hidden">
              <CardContent className="p-5 flex flex-col items-center justify-center gap-2">
                <svg
                  width="31"
                  height="30"
                  viewBox="0 0 31 30"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.69629 1.07141V28.9286H29.0034"
                    stroke="#2155CD"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7.99805 13.9285L13.2494 19.2857L21.6516 6.42853L29.0035 11.7857"
                    stroke="#2155CD"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <p className="font-semibold text-xs text-gray-500">
                  Basic Salary
                </p>
                <div className="font-bold text-2xl">
                  {formatPrice(Number(salary?.total_amount) || 0)}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      {/* Financial transaction */}
      <div className="space-y-4">
        <p className="font-bold text-2xl">Financial transaction record</p>
        {/* Table */}
        <Card className="w-full overflow-auto">
          <table className="w-full text-base overflow-hidden rounded-xl border">
            {/* Table Header */}
            <thead className="bg-secondary">
              <tr>
                <th className="p-2 text-lg text-left border-2 border-muted">
                  Date
                </th>
                <th className="p-2 text-lg text-left border-2 border-muted">
                  Transaction
                </th>
                <th className="p-2 text-lg text-left border-2 border-muted">
                  Amount
                </th>
                <th className="p-2 text-lg text-left border-2 border-muted">
                  Reason
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {loadingTransaction ? (
                <tr className="w-full">
                  <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                    <Skeleton className="w-full h-8 font-medium border-2 border-muted whitespace-nowrap" />
                  </td>
                  <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                    <Skeleton className="w-full h-8 font-medium border-2 border-muted whitespace-nowrap" />
                  </td>
                  <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                    <Skeleton className="w-full h-8 font-medium border-2 border-muted whitespace-nowrap" />
                  </td>
                  <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                    <Skeleton className="w-full h-8 font-medium border-2 border-muted whitespace-nowrap" />
                  </td>
                </tr>
              ) : transaction.length !== 0 ? (
                transaction.map((item, index) => (
                  <tr key={index} className="border-2 border-muted">
                    <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                      {item.month}
                    </td>
                    <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                      {item.type.toUpperCase()}
                    </td>
                    <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                      {formatPrice(item.amount)}
                    </td>
                    <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                      {item.reason}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={4}
                    className="p-2 font-medium border-2 border-muted text-center text-lg h-20"
                  >
                    There are no transactions ^_____^
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>

      {isAdmin && (
        <>
          {/* Communication */}
          <div className="space-y-4">
            <p className="font-bold text-2xl">Communicate with the teacher</p>
            <div className="bg-secondary rounded-xl w-full p-4 space-y-4 relative">
              <textarea
                name="message"
                id="message"
                cols="30"
                rows="5"
                placeholder="What's on your mind?"
                className="bg-transparent w-full h-full outline-none"
              />
              <div className="flex items-center justify-between gap-4 absolute bottom-3 left-4 right-3">
                <div className="cursor-pointer">
                  <Paperclip className="size-5" />
                </div>
                <Button
                  size="icon"
                  className="rounded-full bg-blue-600 hover:bg-blue-600/80 text-white"
                >
                  <Send />
                </Button>
              </div>
            </div>
          </div>
          {/* Actions */}
          <div className="flex items-center justify-end flex-wrap sm:flex-nowrap gap-4 max-w-2xl mx-auto !mt-10">
            <Button variant="destructive" className="w-full h-11 rounded-xl">
              Teacher Ban
            </Button>
            <Button
              variant="outline"
              className="w-full h-11 border-2 rounded-xl"
            >
              Account suspension
            </Button>
          </div>
        </>
      )}
      {!isSameUser && <RateButton profileData={teacherData} />}
    </div>
  );
}

const additionComponents = () => (
  <>
    {/* Basic Contract Data */}
    <div className="space-y-4">
      <p className="font-bold text-2xl">Basic Contract Data</p>
      <div className="border-2 bg-secondary p-4 shadow-sm rounded-xl grid grid-cols-1 md:grid-cols-2 gap-4 font-medium sm:text-lg">
        <p>
          Contract number: <span className="font-bold">#TCH2025-001</span>
        </p>
        <p>
          Contract duration:{" "}
          <span className="font-bold">One year renewable</span>
        </p>
        <p>
          Agreed Salary:{" "}
          <span className="font-bold">Only 15,000 Egyptian pounds</span>
        </p>
        <p>
          Expiry date: <span className="font-bold">March 21, 2026</span>
        </p>
        <p>
          Start date: <span className="font-bold">March 21, 2025</span>
        </p>
        <p>
          Contract Status:{" "}
          <span className="font-bold text-green-500">Valid</span>
        </p>
      </div>
      {/* Actions */}
      <div className="flex items-center justify-end flex-wrap sm:flex-nowrap gap-4">
        <Button className="w-full h-11 bg-blue-600 text-white hover:bg-blue-600/80 rounded-full">
          View full contract
        </Button>
        <Button
          variant="outline"
          className="w-full h-11 border-2 border-blue-600 text-blue-600 hover:text-blue-500 rounded-full"
        >
          Download the contract
        </Button>
      </div>
    </div>
    ;{/* Record of amendments */}
    <div className="space-y-4">
      <p className="font-bold text-2xl">Record of amendments to the contract</p>
      <Card className="w-full overflow-auto">
        <table className="w-full text-base overflow-hidden rounded-xl border">
          {/* Table Header */}
          <thead className="bg-secondary">
            <tr>
              <th className="p-2 text-lg text-left border-2 border-muted">
                Edit Number
              </th>
              <th className="p-2 text-lg text-left border-2 border-muted">
                Date
              </th>
              <th className="p-2 text-lg text-left border-2 border-muted">
                Edit Type
              </th>
              <th className="p-2 text-lg text-left border-2 border-muted">
                Notes
              </th>
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {[
              {
                editNumber: "#1",
                date: "March 14, 2025",
                editType: "Contract Renewal",
                notes: "Renewed for one year",
              },
              {
                editNumber: "#2",
                date: "March 14, 2025",
                editType: "Salary Adjustment",
                notes: "Increased salary to 18,000 EGP",
              },
              {
                editNumber: "#3",
                date: "March 14, 2025",
                editType: "Contract Duration Adjustment",
                notes: "Contract is now non-renewable",
              },
              {
                editNumber: "#4",
                date: "March 14, 2025",
                editType: "Teacher Information Update",
                notes: "Updated email and phone number",
              },
              {
                editNumber: "#5",
                date: "March 14, 2025",
                editType: "Teacher Information Update",
                notes: "Updated email and phone number",
              },
            ].map((item, index) => (
              <tr key={index} className="border-2 border-muted">
                <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                  {item.editNumber}
                </td>
                <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                  {item.date}
                </td>
                <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                  {item.editType}
                </td>
                <td className="p-2 font-medium border-2 border-muted whitespace-nowrap">
                  {item.notes}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  </>
);
